<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  content: {
    type: String,
    default: '',
  },
  interactiveConfig: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const cfg = computed(() => props.interactiveConfig || {});

const hasMedia = computed(() => !!cfg.value.media?.url);
const hasTitle = computed(() => !!cfg.value.title);
const hasFooter = computed(() => !!cfg.value.footer);
const hasButtons = computed(
  () => cfg.value.buttons && cfg.value.buttons.length > 0
);
const hasAnyInteractive = computed(
  () => hasMedia.value || hasTitle.value || hasFooter.value || hasButtons.value
);

const mediaType = computed(() => cfg.value.media?.type || 'image');

const formattedContent = computed(() => {
  if (!props.content) return '';

  let html = props.content;

  // Escape HTML first
  html = html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // WhatsApp formatting
  // Code blocks (``` ```)
  html = html.replace(/```([^`]+)```/g, '<code class="wa-code">$1</code>');
  // Bold (*text*)
  html = html.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
  // Italic (_text_)
  html = html.replace(/_([^_]+)_/g, '<em>$1</em>');
  // Strikethrough (~text~)
  html = html.replace(/~([^~]+)~/g, '<s>$1</s>');
  // Placeholders ({{key}})
  html = html.replace(
    /\{\{(\w+)\}\}/g,
    '<span class="wa-placeholder">$1</span>'
  );
  // Line breaks
  html = html.replace(/\n/g, '<br>');

  return html;
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="phone-preview">
    <div class="phone-frame">
      <div class="screen-notch" />
      <div class="chat-container">
        <!-- Status bar -->
        <div class="status-bar">
          <div class="status-left">
            <div class="signal-bars">
              <span class="bar active" />
              <span class="bar active" />
              <span class="bar" />
              <span class="bar" />
            </div>
            <span class="carrier">Claro</span>
          </div>
          <div class="status-center">
            <span class="time">21:00</span>
          </div>
          <div class="status-right">
            <div class="status-icons">
              <div class="wifi-icon">
                <div class="wifi-bar" />
                <div class="wifi-bar" />
                <div class="wifi-bar" />
              </div>
              <div class="battery-icon">
                <div class="battery-body">
                  <div class="battery-level" />
                </div>
                <div class="battery-tip" />
              </div>
              <span class="battery-percent">85%</span>
            </div>
          </div>
        </div>

        <!-- Chat header -->
        <div class="chat-header">
          <div class="header-left">
            <div class="back-arrow">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </div>
            <div class="contact-info">
              <div class="contact-avatar">
                <div class="avatar-circle">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
                    />
                  </svg>
                </div>
              </div>
              <div class="contact-details">
                <span class="contact-name">Contato</span>
                <span class="contact-status">online</span>
              </div>
            </div>
          </div>
          <div class="header-actions">
            <div class="action-icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="1" />
                <circle cx="19" cy="12" r="1" />
                <circle cx="5" cy="12" r="1" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Chat content -->
        <div class="chat-content">
          <div v-if="content || hasAnyInteractive" class="message-content">
            <!-- Media preview -->
            <div v-if="hasMedia" class="media-preview">
              <template v-if="mediaType === 'image'">
                <div class="media-image">
                  <span class="i-lucide-image size-8 opacity-60" />
                </div>
              </template>
              <template v-else-if="mediaType === 'video'">
                <div class="media-video">
                  <span class="i-lucide-play-circle size-8 opacity-60" />
                </div>
              </template>
              <template v-else>
                <div class="media-document">
                  <span class="i-lucide-file-text size-5 opacity-80" />
                  <span class="text-xs opacity-80 truncate max-w-[120px]">
                    {{ cfg.media?.fileName || 'document' }}
                  </span>
                </div>
              </template>
            </div>

            <!-- Title -->
            <p v-if="hasTitle" class="message-title">
              {{ cfg.title }}
            </p>

            <!-- Body -->
            <p v-if="content" class="message-text" v-html="formattedContent" />

            <!-- Footer -->
            <p v-if="hasFooter" class="message-footer">
              {{ cfg.footer }}
            </p>

            <!-- Time -->
            <div class="message-time">
              <span>21:02</span>
              <div class="message-status">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M20 6L9 17l-5-5" />
                  <path d="M16 6L9 13" />
                </svg>
              </div>
            </div>

            <!-- Buttons -->
            <div v-if="hasButtons" class="buttons-preview">
              <template v-for="(btn, idx) in cfg.buttons" :key="idx">
                <div v-if="btn.type === 'list'" class="btn-pill btn-list">
                  <span class="i-lucide-list size-3" />
                  {{ btn.displayText || 'List' }}
                </div>
                <div v-else-if="btn.type === 'url'" class="btn-pill btn-url">
                  <span class="i-lucide-external-link size-3" />
                  {{ btn.displayText || 'URL' }}
                </div>
                <div v-else-if="btn.type === 'copy'" class="btn-pill btn-copy">
                  <span class="i-lucide-copy size-3" />
                  {{ btn.displayText || 'Copy' }}
                </div>
                <div v-else class="btn-pill btn-reply">
                  {{ btn.displayText || 'Reply' }}
                </div>
              </template>
            </div>
          </div>
          <div v-else class="empty-preview">
            <p class="text-xs text-n-slate-9 text-center px-4">
              {{ t('WHATSAPP_APP_TEMPLATES.PREVIEW.EMPTY') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.phone-frame {
  @apply rounded-3xl w-full max-w-80 h-[560px] relative overflow-hidden;
  border: 2px solid theme('colors.n.slate.6');
  background: theme('colors.n.slate.2');
  box-shadow:
    0 0 0 1px theme('colors.n.alpha.1'),
    0 20px 60px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 theme('colors.n.alpha.white');
}

.screen-notch {
  @apply absolute top-2 left-1/2 transform -translate-x-1/2 w-32 h-6 z-10;
  background: theme('colors.n.slate.12');
  border-radius: 0 0 16px 16px;
}

.chat-container {
  @apply h-full flex flex-col overflow-hidden relative;
  background: theme('colors.n.slate.1');
}

.status-bar {
  @apply flex justify-between items-center px-5 py-3 mt-7 text-sm font-semibold;
  color: theme('colors.n.slate.12');
}

.status-left {
  @apply flex items-center gap-2;
}

.signal-bars {
  @apply flex gap-0.5 items-end;
}

.signal-bars .bar {
  @apply w-0.5 rounded-sm;
  background: theme('colors.n.slate.6');
}
.signal-bars .bar:nth-child(1) {
  @apply h-1;
}
.signal-bars .bar:nth-child(2) {
  @apply h-1.5;
}
.signal-bars .bar:nth-child(3) {
  @apply h-2;
}
.signal-bars .bar:nth-child(4) {
  @apply h-2.5;
}
.signal-bars .bar.active {
  background: theme('colors.n.slate.12');
}

.carrier {
  @apply text-xs font-medium;
  color: theme('colors.n.slate.11');
}

.status-center .time {
  @apply text-sm font-semibold;
  color: theme('colors.n.slate.12');
}

.status-right {
  @apply flex items-center gap-1;
}
.status-icons {
  @apply flex items-center gap-1.5;
}

.wifi-icon {
  @apply flex gap-px items-end;
}
.wifi-bar {
  @apply w-0.5 rounded-sm;
  background: theme('colors.n.slate.12');
}
.wifi-bar:nth-child(1) {
  @apply h-0.5;
}
.wifi-bar:nth-child(2) {
  @apply h-1;
}
.wifi-bar:nth-child(3) {
  @apply h-1.5;
}

.battery-icon {
  @apply flex items-center;
}
.battery-body {
  @apply w-5 h-2.5 rounded-sm relative;
  border: 1px solid theme('colors.n.slate.12');
}
.battery-level {
  @apply w-[85%] h-full rounded-sm;
  background: theme('colors.n.teal.9');
}
.battery-tip {
  @apply w-0.5 h-1.5 ml-px;
  background: theme('colors.n.slate.12');
  border-radius: 0 1px 1px 0;
}
.battery-percent {
  @apply text-xs font-medium ml-1;
  color: theme('colors.n.slate.11');
}

.chat-header {
  @apply px-4 py-3 flex items-center justify-between;
  background: theme('colors.n.slate.2');
  color: theme('colors.n.slate.12');
  border-bottom: 1px solid theme('colors.n.slate.6');
}

.header-left {
  @apply flex items-center gap-3;
}
.back-arrow {
  @apply p-1 rounded-full;
  color: theme('colors.n.teal.9');
}

.contact-info {
  @apply flex items-center gap-3;
}
.contact-avatar .avatar-circle {
  @apply w-9 h-9 rounded-full flex items-center justify-center;
  background: linear-gradient(
    135deg,
    theme('colors.n.teal.9'),
    theme('colors.n.teal.8')
  );
  color: white;
}
.contact-details {
  @apply flex flex-col gap-0.5;
}
.contact-name {
  @apply font-semibold text-xs;
  color: theme('colors.n.slate.12');
}
.contact-status {
  @apply text-xs font-medium;
  color: theme('colors.n.teal.9');
}

.header-actions {
  @apply flex gap-4 mb-2;
}
.action-icon {
  @apply p-1 ml-2 rounded-full;
  color: theme('colors.n.teal.9');
}

.chat-content {
  @apply flex-1 min-h-0 px-2 py-2 overflow-y-auto relative;
  background: linear-gradient(
    to bottom,
    theme('colors.n.slate.1'),
    theme('colors.n.slate.2')
  );
}

.chat-content::before {
  @apply absolute inset-0 pointer-events-none;
  content: '';
  opacity: 0.3;
  background-image: radial-gradient(
    circle at 1px 1px,
    theme('colors.n.slate.6') 1px,
    transparent 0
  );
  background-size: 20px 20px;
}

.empty-preview {
  @apply flex items-center justify-center h-full relative z-[1];
}

.message-content {
  @apply rounded-2xl px-4 py-3 pb-2 max-w-[100%] relative overflow-y-auto;
  background: theme('colors.n.teal.9');
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.chat-content {
  scrollbar-width: thin;
  scrollbar-color: rgb(var(--slate-6)) transparent;
}

.chat-content::-webkit-scrollbar {
  width: 6px;
}

.chat-content::-webkit-scrollbar-track {
  background: transparent;
}

.chat-content::-webkit-scrollbar-thumb {
  background-color: rgb(var(--slate-6));
  border-radius: 9999px;
}

.chat-content::-webkit-scrollbar-thumb:hover {
  background-color: rgb(var(--slate-7));
}

.message-content {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.45) transparent;
}

.message-content::-webkit-scrollbar {
  width: 6px;
}

.message-content::-webkit-scrollbar-track {
  background: transparent;
}

.message-content::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.45);
  border-radius: 9999px;
}

.message-content::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.65);
}

/* Media preview */
.media-preview {
  @apply -mx-4 -mt-3 mb-2;
}

.media-image {
  @apply w-full h-24 flex items-center justify-center;
  background: rgba(255, 255, 255, 0.1);
}

.media-video {
  @apply w-full h-24 flex items-center justify-center;
  background: rgba(0, 0, 0, 0.2);
}

.media-document {
  @apply flex items-center gap-2 px-4 py-2;
  background: rgba(255, 255, 255, 0.1);
}

/* Title */
.message-title {
  @apply m-0 mb-1 text-sm font-bold leading-snug;
  color: inherit;
}

.message-text {
  @apply m-0 mb-1 text-sm leading-relaxed font-normal break-words whitespace-break-spaces;
  color: inherit;
}

.message-text :deep(.wa-code) {
  @apply px-1 py-0.5 rounded text-xs;
  background: rgba(255, 255, 255, 0.15);
  font-family: monospace;
}

.message-text :deep(.wa-placeholder) {
  @apply px-1 py-0.5 rounded text-xs font-medium;
  background: rgba(255, 255, 255, 0.25);
}

/* Footer */
.message-footer {
  @apply m-0 mb-1 text-xs leading-snug;
  color: rgba(255, 255, 255, 0.6);
}

.message-time {
  @apply flex items-center justify-end gap-1 mt-1;
}
.message-time span {
  @apply text-xs font-normal;
  color: rgba(255, 255, 255, 0.7);
}
.message-status {
  @apply flex items-center;
  color: theme('colors.n.blue.4');
}

/* Buttons preview */
.buttons-preview {
  @apply flex flex-wrap gap-1.5 mt-2 -mx-4 -mb-2 px-3 py-2 border-t;
  border-color: rgba(255, 255, 255, 0.15);
}

.btn-pill {
  @apply flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium cursor-default;
  background: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.9);
}

.btn-pill.btn-url {
  color: rgba(200, 230, 255, 1);
}

.btn-pill.btn-copy {
  color: rgba(200, 255, 200, 1);
}

.btn-pill.btn-list {
  @apply w-full justify-center;
}

@media (max-width: 1024px) {
  .phone-frame {
    @apply max-w-[280px] h-[440px];
  }
  .message-text {
    @apply text-xs;
  }
}

@media (max-width: 768px) {
  .phone-frame {
    @apply max-w-[260px] h-[400px];
  }
  .status-bar {
    @apply px-4 py-2.5 pt-2 text-xs;
  }
  .contact-name {
    @apply text-sm;
  }
  .message-text {
    @apply text-xs;
  }
}
</style>
